import { useEffect } from "react";
import { ExternalLink } from "lucide-react";
import { Link, useLocation } from "react-router";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import type { ManifestObject } from "@shared-core/domain/object-ui/contract";
import {
  FEEDBACK_OBJECT_ID,
  hubFeedbackListHref,
} from "@shared-core/domain/feedback-quick-note";
import {
  emptyListQuery,
  formatListQuery,
  withQueue,
} from "@shared-core/domain/object-ui/list-query";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { Logo } from "../../Logo";
import {
  useCanCurateCatalogue,
  useCanManageAutomaticActions,
  useCanManageBuddyLists,
  useCanManageDepartments,
  useCanManageEmailCaptureExclusions,
  useCanManageTargets,
  useCanManageTicketCategories,
  useCanReadFeatures,
  useCanReadForecasting,
  useCanReadManufacturingSetup,
  useCanReadTargets,
  useIsAdmin,
} from "../../../auth/hooks";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "../../primitives/sidebar";
import {
  oneNav,
  type NavAccess,
  type NavEntry,
  type PageEntry,
} from "./one-nav";

/** A finger-sized line in the phone's sheet; the desktop rail keeps shadcn's 32px. */
const TAP = "max-md:h-10";

/**
 * The line of the section the person is in. shadcn's own fill (sidebar-accent, a hair darker than the sidebar) is too
 * faint to find, so the line also gets a tinted fill in the primary colour, a bar on its left edge and a heavier
 * weight, in both themes; the page link carries `aria-current` for a screen reader.
 */
const CURRENT =
  "relative data-[active=true]:bg-sidebar-primary/15 data-[active=true]:font-semibold data-[active=true]:before:absolute data-[active=true]:before:inset-y-1 data-[active=true]:before:left-0 data-[active=true]:before:w-1 data-[active=true]:before:rounded-full data-[active=true]:before:bg-sidebar-primary";

/**
 * One's sidebar (04_frontend_spec.md §6.2; spec 17 §2.4): the areas the
 * manifest carries, each a heading over its objects, then More (the CIS pages
 * that are not objects) and Setup. Money ends with its two workbenches;
 * Insights is one line straight to its page. A page entry is also hidden from
 * a person its page would turn away. shadcn's Sidebar as it comes, collapsing
 * to icons. The sidebar lists objects only, each one line to its list: queues
 * live on the list page as chips with their counts (Ben 18 Oct), so the
 * sidebar fetches no counts. The one exception is Support's "My reminders", a
 * standalone queue line because notes have no sidebar object (Ben 16 Oct). An
 * object whose feature is off for the person is still drawn, muted and tagged
 * "Switched off" (hide or block, Ben 16 Oct).
 */
export function ObjectSidebar() {
  const index = useObjectManifest();
  const hasAccess = useNavAccess();
  const isPageOffered = (entry: PageEntry) =>
    isRouteEnabled(entry.route) &&
    (entry.access === undefined || hasAccess[entry.access]);
  const headings = oneNav(index.manifest.areas)
    .map((heading) => ({
      ...heading,
      link:
        heading.link && isPageOffered(heading.link) ? heading.link : undefined,
      entries: heading.entries.filter((entry) => {
        if (entry.kind === "page") return isPageOffered(entry);
        if (!index.isObjectKnown(entry.object)) return false;
        if (entry.kind === "object" && entry.access && !hasAccess[entry.access])
          return false;
        const object = index.objectOf(entry.object);
        if (!object.list) return false;
        if (entry.kind === "object") return true;
        // A queue line stands in for an object that is not itself in the sidebar.
        return (
          index.isObjectEnabled(entry.object) &&
          object.list.queues.some((q) => q.id === entry.queue)
        );
      }),
    }))
    .filter((heading) => heading.link || heading.entries.length > 0);

  // On a phone the sidebar is a sheet over the page: once a line is chosen
  // it gets out of the way, as a native menu would.
  const { pathname, search } = useLocation();
  const { setOpenMobile } = useSidebar();
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, search, setOpenMobile]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="group-data-[collapsible=icon]:hidden">
        {/* pl-6: the logo pulls itself 20px left (Logo.tsx); this keeps its mark on screen. */}
        <Link to={paths.root()} aria-label="Home" className="pl-6 pr-2 pt-1">
          <Logo size="sm" />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {headings.map((heading) =>
          heading.link ? (
            // A heading that is one line: no label over it, so it reads as a heading of its own and keeps its icon
            // when the sidebar collapses (a group label would vanish).
            <SidebarGroup key={heading.label}>
              <SidebarMenu>
                <NavItem entry={heading.link} />
              </SidebarMenu>
            </SidebarGroup>
          ) : (
            <SidebarGroup key={heading.label}>
              <SidebarGroupLabel>{heading.label}</SidebarGroupLabel>
              <SidebarMenu>
                {heading.entries.map((entry) => (
                  <NavItem
                    key={
                      entry.kind === "page"
                        ? entry.route
                        : entry.kind === "queue"
                          ? `${entry.object}:${entry.queue}`
                          : entry.object
                    }
                    entry={entry}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ),
        )}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}

/** The role checks a page entry can name, each the one its route guard applies. */
function useNavAccess(): Record<NavAccess, boolean> {
  return {
    admin: useIsAdmin(),
    catalogue_curation: useCanCurateCatalogue(),
    departments: useCanManageDepartments(),
    email_capture_exclusions: useCanManageEmailCaptureExclusions(),
    buddy_lists: useCanManageBuddyLists(),
    automatic_actions: useCanManageAutomaticActions(),
    ticket_categories: useCanManageTicketCategories(),
    features: useCanReadFeatures(),
    manufacturing_setup: useCanReadManufacturingSetup(),
    forecasting: useCanReadForecasting(),
    targets_read: useCanReadTargets(),
    targets_manage: useCanManageTargets(),
  };
}

function NavItem({ entry }: { entry: NavEntry }) {
  const { pathname } = useLocation();
  const index = useObjectManifest();
  if (entry.kind === "page") {
    const Icon = entry.icon;
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          className={`${TAP} ${CURRENT}`}
          asChild
          tooltip={entry.label}
          isActive={pathname === entry.path}
        >
          <Link
            to={entry.path}
            aria-current={pathname === entry.path ? "page" : undefined}
          >
            <Icon />
            <span>{entry.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  if (entry.kind === "queue") {
    return (
      <QueueNavItem
        object={index.objectOf(entry.object)}
        queueId={entry.queue}
        icon={entry.icon}
      />
    );
  }
  const object = index.objectOf(entry.object);
  return index.isObjectEnabled(object.id) ? (
    <ObjectNavItem object={object} icon={entry.icon} />
  ) : (
    <SwitchedOffNavItem object={object} icon={entry.icon} />
  );
}

/** Opens the object's "switched off" page. */
function SwitchedOffNavItem({
  object,
  icon: Icon,
}: {
  object: ManifestObject;
  icon: NavEntry["icon"];
}) {
  const { pathname } = useLocation();
  const listPath = paths.objectList({ objectId: object.id });
  const isCurrent =
    pathname === listPath || pathname.startsWith(`${listPath}/`);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={`${object.plural} (switched off)`}
        isActive={isCurrent}
        className={`${TAP} ${CURRENT} text-muted-foreground`}
      >
        <Link to={listPath} aria-current={isCurrent ? "true" : undefined}>
          <Icon />
          <span className="min-w-0 truncate">{object.plural}</span>
          <span className="ml-auto shrink-0 text-xs">Switched off</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** One shared queue of an object that is not itself in the sidebar, as a single line with no count. */
function QueueNavItem({
  object,
  queueId,
  icon: Icon,
}: {
  object: ManifestObject;
  queueId: string;
  icon: NavEntry["icon"];
}) {
  const { pathname, search } = useLocation();
  const queue = object.list?.queues.find((q) => q.id === queueId);
  if (!queue) return null;
  const listPath = paths.objectList({ objectId: object.id });
  const isCurrent =
    pathname === listPath &&
    new URLSearchParams(search).get("queue") === queue.id;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        className={`${TAP} ${CURRENT}`}
        asChild
        tooltip={queue.label}
        isActive={isCurrent}
      >
        <Link
          aria-current={isCurrent ? "page" : undefined}
          to={paths.objectList({
            objectId: object.id,
            search: formatListQuery(withQueue(emptyListQuery(), queue)),
          })}
        >
          <Icon />
          <span>{queue.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ObjectNavItem({
  object,
  icon: Icon,
}: {
  object: ManifestObject;
  icon: NavEntry["icon"];
}) {
  const { pathname } = useLocation();
  const listPath = paths.objectList({ objectId: object.id });
  const isCurrent =
    pathname === listPath || pathname.startsWith(`${listPath}/`);
  // Quick notes are kept in production only (feedback spec 01 §3): anywhere else this line opens that list in a new
  // tab, so nobody reads an empty local list by mistake.
  const hubList =
    object.id === FEEDBACK_OBJECT_ID
      ? hubFeedbackListHref(
          getRuntimeConfig().feedbackHubApiBaseUrl,
          hubSearch(object),
        )
      : null;
  if (hubList !== null) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          className={`${TAP} ${CURRENT}`}
          asChild
          tooltip={`${object.plural} (opens in production)`}
        >
          <a href={hubList} target="_blank" rel="noreferrer">
            <Icon />
            <span>{object.plural}</span>
            <ExternalLink
              className="ml-auto size-3 shrink-0 text-muted-foreground"
              aria-hidden
            />
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        className={`${TAP} ${CURRENT}`}
        asChild
        tooltip={object.plural}
        isActive={isCurrent}
      >
        <Link to={listPath} aria-current={isCurrent ? "true" : undefined}>
          <Icon />
          <span>{object.plural}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** The hub's Feedback list opens on the person's own notes. */
function hubSearch(object: ManifestObject): string | undefined {
  const mine = object.list?.queues.find((queue) => queue.id === "mine");
  return mine ? formatListQuery(withQueue(emptyListQuery(), mine)) : undefined;
}
